import { FunctionComponent, useReducer } from 'react';
import { DraftConfiguration } from '@/types';
import { assertNever } from '@/utils/assertNever';
import { t, tWithCount } from '@/utils/i18n';
import {
  isDraftBendingTextConfiguration,
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { getProductSavingMessage } from '@/model/product/getProductSavingMessage';
import {
  AlertModal,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@/components/common/modal/modal';
import './SaveDialog.scss';

export interface SaveDialogState {
  loading: boolean;
  error?: unknown;
  configurationsToSave: number;
  savedConfigurations: number;
}

interface SaveStartDialogStateAction {
  type: 'save-start';
  payload: DraftConfiguration[];
}

interface SaveUpdateDialogStateAction {
  type: 'save-update';
}

interface SaveSuccessDialogStateAction {
  type: 'save-success';
}

interface SaveErrorDialogStateAction {
  type: 'save-error';
  payload: unknown;
}

interface SaveErrorDismissDialogStateAction {
  type: 'save-error-dismiss';
}

export type SaveDialogStateAction =
  | SaveStartDialogStateAction
  | SaveUpdateDialogStateAction
  | SaveSuccessDialogStateAction
  | SaveErrorDialogStateAction
  | SaveErrorDismissDialogStateAction;

const saveDialogReducer = (state: SaveDialogState, action: SaveDialogStateAction) => {
  switch (action.type) {
    case 'save-start': {
      const configurationsToSave = action.payload.filter(
        (configuration) =>
          isDraftBendingTextConfiguration(configuration) ||
          isDraftSpecialTextConfiguration(configuration) ||
          (isDraftDesignConfiguration(configuration) &&
            Boolean(configuration.filter || configuration.mask))
      ).length;
      return { ...state, loading: true, configurationsToSave, savedConfigurations: 0 };
    }
    case 'save-update':
      return {
        ...state,
        savedConfigurations: Math.min(state.savedConfigurations + 1, state.configurationsToSave),
      };
    case 'save-success':
      return { ...state, loading: false, configurationsToSave: 0, savedConfigurations: 0 };
    case 'save-error':
      return {
        ...state,
        loading: false,
        error: action.payload,
        configurationsToSave: 0,
        savedConfigurations: 0,
      };
    case 'save-error-dismiss':
      return { ...state, error: undefined };
    default:
      assertNever(action);
  }
};

const initialState: SaveDialogState = {
  loading: false,
  configurationsToSave: 0,
  savedConfigurations: 0,
};

export function useSaveDialog() {
  return useReducer(saveDialogReducer, initialState);
}

interface Props {
  saveState: SaveDialogState;
  dispatch: (action: SaveDialogStateAction) => void;
}

export const SaveDialog: FunctionComponent<Props> = ({ dispatch, saveState }) => {
  const { error } = saveState;
  if (error) {
    const [errorMessage] = getProductSavingMessage(error);
    return (
      <AlertModal
        title={t('errorDialog.title')}
        message={t('errorDialog.button')}
        onAccept={() => dispatch({ type: 'save-error-dismiss' })}
      >
        <p>{errorMessage}</p>
      </AlertModal>
    );
  }

  const { configurationsToSave } = saveState;
  if (configurationsToSave) {
    return (
      <Modal className="save-modal">
        <ModalHeader>
          <ModalTitle>{t('savingDialog.title')}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="progress">
            <div className="progress__bar" />
          </div>
          <p>{t('savingDialog.saving')}</p>
          <p>
            {tWithCount(
              configurationsToSave,
              'savingDialog.status',
              saveState.savedConfigurations.toString(),
              configurationsToSave.toString()
            )}
          </p>
        </ModalContent>
      </Modal>
    );
  }

  return null;
};
